import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from 'src/components/Loader';
import PaginatedDataGrid, { PaginatedDataGridColumn } from 'src/components/paginated-data-grid/PaginatedDataGrid';
import {
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Button,
  Divider,
  Stack,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import { DataService } from 'src/services/data-service';
import type { StorageUnitDetail, StorageUnitDetailTableRow } from 'src/types/StorageUnit.types';
import CreateStorageUnitDrawer from './CreateStorageUnitDrawer';
import StorageUnitTable from 'src/components/common/StorageUnitTable';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import EditIcon from '@mui/icons-material/Edit';
import TransformIcon from '@mui/icons-material/Transform';
import UpdateStorageUnitDrawer from './UpdateStorageUnitDrawer';
import { useAppSelector } from 'src/redux/hooks';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import StatusCard from 'src/components/StatusCard';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { ArrowBack } from '@mui/icons-material';

interface DrawerStorageUnit {
  Id: string;
  Code: string;
  Name: string;
  ParentWarehouseId: string;
  ParentStorageUnitId?: string;
  Level: number;
  IsLeaf: boolean;
}

interface Transfer {
  id: string;
  itemId: string;
  itemName: string;
  itemBarcode?: string;
  quantity: number;
  transferType: number;
  direction: number;
  sourceType: number;
  sourceId: string;
  sourceName: string;
  destinationType: number;
  destinationId: string;
  destinationName: string;
  timestamp: string;
  notes: string;
  referenceId: string;
}

const LS_KEY_STORAGE = 'storage-unit-dynamic-prop-keys';

const canonicalize = (k: string) => k?.toLowerCase().trim();

const getCaseInsensitive = (obj: Record<string, any> | undefined, canonicalKey: string) => {
  if (!obj) return null;
  const exact = obj[canonicalKey];
  if (exact !== undefined) return exact;

  const found = Object.entries(obj).find(([kk]) => canonicalize(kk) === canonicalKey);
  return found ? found[1] : null;
};

const StorageUnitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.user);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StorageUnitDetail | null>(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState<StorageUnitDetail | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Dynamic columns state
  const [storageItemColumns, setStorageItemColumns] = useState<PaginatedDataGridColumn[]>([]);

  const nav = useNavigate();

  // Initialize seen keys from localStorage
  const initialSeenKeysStorage = (() => {
    try {
      const raw = localStorage.getItem(LS_KEY_STORAGE);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      const uniq = Array.isArray(arr) ? Array.from(new Set(arr.map((k: string) => canonicalize(k)))) : [];
      return uniq;
    } catch {
      return [];
    }
  })();

  const seenDynamicKeysStorageRef = useRef<Set<string>>(new Set(initialSeenKeysStorage));

  const persistSeenKeysStorage = (set: Set<string>) => {
    try {
      localStorage.setItem(LS_KEY_STORAGE, JSON.stringify(Array.from(set)));
    } catch {}
  };

  // Format headers nicely
  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const specialCases: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season',
    };
    return specialCases[key] || formatted;
  }, []);

  // Build columns with dynamic properties
  const buildStorageItemColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const baseColumns: PaginatedDataGridColumn[] = [
        {
          field: 'unitBarcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: params.row.itemId ? 'pointer' : 'default', fontWeight: 'medium' }}
              onClick={(event) => {
                event.stopPropagation();
                if (params.row.itemId) navigate(`/Units/${params.row.unitId}`);
              }}
            >
              {params.value || 'N/A'}
            </Typography>
          ),
        },
        {
          field: 'itemName',
          headerName: 'Product Name',
          flex: 1.5,
          minWidth: 200,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              sx={{
                cursor: params.row.itemId ? 'pointer' : 'default',
                '&:hover': { textDecoration: params.row.itemId ? 'underline' : 'none' },
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (params.row.itemId) navigate(`/units/${params.row.unitId}`);
              }}
            >
              {params.value || 'Unknown Product'}
            </Typography>
          ),
        },
        {
          field: 'unitSymbol',
          headerName: 'Symbol',
          flex: 1,
          minWidth: 120,
          renderCell: (params: GridRenderCellParams) => (
            <Typography variant="body2">{params.value || '-'}</Typography>
          ),
        },
        {
          field: 'boxBarcode',
          headerName: 'Box Barcode',
          flex: 1.2,
          minWidth: 160,
          renderCell: (params: GridRenderCellParams) => (
            <Typography
              sx={{
                cursor: params.value ? 'pointer' : 'default',
                '&:hover': { textDecoration: params.value ? 'underline' : 'none' },
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (params.value) navigate(`/box/${params.value}`);
              }}
            >
              {params.value || '-'}
            </Typography>
          ),
        },
        {
          field: 'quantity',
          headerName: 'Quantity',
          width: 100,
          type: 'number',
          renderCell: (params: GridRenderCellParams) => (
            <Chip label={params.value ?? 0} color="primary" variant="outlined" size="small" />
          ),
        },
      ];

      // Add dynamic columns
      const dynamicCols = dynamicKeys.sort().map((ck) => {
        const prettyHeader = formatPropertyName(ck);

        const col: PaginatedDataGridColumn = {
          field: ck,
          headerName: prettyHeader,
          flex: 1,
          minWidth: 110,
          valueGetter: (params) => getCaseInsensitive(params.row.itemAdditionalProperties, ck),
          sortable: true,
        };

        // Color columns with color preview
        if (ck.includes('color')) {
          col.columnType = 'color';
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            const colorValue = colorParser(params.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2">{params.value}</Typography>
              </Box>
            );
          };
        }

        // Category/Brand/Season as chips
        if (['category', 'maincategory', 'brand', 'season'].some((f) => ck.includes(f))) {
          col.renderCell = (params: GridRenderCellParams) => {
            if (!params.value) return <Typography color="text.secondary">-</Typography>;
            return <Chip label={params.value} size="small" variant="outlined" color="primary" />;
          };
        }

        return col;
      });

      const staticColumns: PaginatedDataGridColumn[] = [
        { field: 'createdAt', headerName: 'Added Date', width: 160, type: 'dateTime' },
        { field: 'updatedAt', headerName: 'Last Updated', width: 160, type: 'dateTime' },
      ];

      return [...baseColumns, ...dynamicCols, ...staticColumns];
    },
    [formatPropertyName, navigate]
  );

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await DataService.get(`api/StorageUnit/${id}`, {});
      const json = await res.json();
      if (json.success && json.data) {
        setData(json.data);
        
        // Process dynamic keys from storage items
        const storageItems = Array.isArray(json.data.storageItems) ? json.data.storageItems : [];
        const newKeys: string[] = [];
        
        for (const item of storageItems) {
          if (item.itemAdditionalProperties) {
            Object.keys(item.itemAdditionalProperties).forEach((k) => {
              const ck = canonicalize(k);
              if (!seenDynamicKeysStorageRef.current.has(ck)) {
                seenDynamicKeysStorageRef.current.add(ck);
                newKeys.push(ck);
              }
            });
          }
        }

        if (newKeys.length > 0) {
          persistSeenKeysStorage(seenDynamicKeysStorageRef.current);
        }
        
        // Always rebuild columns to ensure they're up to date
        setStorageItemColumns(buildStorageItemColumns(Array.from(seenDynamicKeysStorageRef.current)));
      } else {
        setNotFound(true);
        setError(json.message || 'Storage unit not found');
      }
    } catch (e) {
      setError('Failed to load storage unit details');
    } finally {
      setLoading(false);
    }
  }, [id, buildStorageItemColumns]);

  // Initialize columns on component mount
  useEffect(() => {
    setStorageItemColumns(buildStorageItemColumns(Array.from(seenDynamicKeysStorageRef.current)));
  }, [buildStorageItemColumns]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const childUnitsList = useMemo(() => {
    if (!data) return [];
    const arr = (data as any).childUnits ?? (data as any).storageUnits ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [data]);

  const storageItemsList = useMemo(() => {
    if (!data) return [];
    return Array.isArray((data as any).storageItems) ? (data as any).storageItems : [];
  }, [data]);

  const hasInventoryItems = storageItemsList.length > 0;
  const hasChildUnits = childUnitsList.length > 0;
  const hasNeither = !hasInventoryItems && !hasChildUnits;

  const permissions = useMemo(() => {
    const role = user.user.role as ROLE;
    const perm = PermissionService.getInstance();
    return {
      canAdd: perm.isAllowed(ACTION.CREATE_STORAGE_UNIT, role),
      canEdit: perm.isAllowed(ACTION.EDIT_STORAGE_UNIT, role),
      canDelete: perm.isAllowed(ACTION.DELETE_STORAGE_UNIT, role),
    };
  }, [user.user.role]);

  const tableData = useMemo<Record<string, StorageUnitDetailTableRow>>(() => {
    if (!data) return {};
    return Object.fromEntries(
      childUnitsList.map((su: any) => [
        su.id,
        {
          id: su.id,
          code: su.code,
          name: su.name,
          warehouseName: data.parentWarehouseName ?? (data as any).warehouse?.name ?? '-',
          parentStorageUnitName: su.parentStorageUnitName || data.name || '-',
          level: su.level,
          isLeaf: su.isLeaf,
          UniqueProducts: (su as any).aggregatedUniqueProducts ?? (su as any).uniqueProducts ?? 0,
          TotalQuantity: (su as any).aggregatedTotalQuantity ?? (su as any).totalQuantity ?? 0,
        },
      ])
    );
  }, [data, childUnitsList]);

  const drawerStorageUnits = useMemo<DrawerStorageUnit[]>(() => {
    if (!data) return [];
    return childUnitsList.map((su: any) => ({
      Id: su.id,
      Code: su.code,
      Name: su.name,
      ParentWarehouseId:
        su.warehouseId ?? (data as any).warehouse?.id ?? '',
      ParentStorageUnitId: su.parentStorageUnitId,
      Level: su.level,
      IsLeaf: su.isLeaf,
    }));
  }, [data, childUnitsList]);

  const handleEdit = useCallback(
    (rowId: string) => {
      const found = drawerStorageUnits.find((su) => su.Id === rowId);
      if (found) {
        setEditData({
          id: found.Id,
          code: found.Code,
          name: found.Name,
          warehouseId: found.ParentWarehouseId,
          parentStorageUnitId: found.ParentStorageUnitId,
          level: found.Level,
          isLeaf: found.IsLeaf,
          parentWarehouseName: '',
          storageUnits: [],
          childUnits: [],
          parentStorageUnitName: found.ParentStorageUnitId ? found.Name : null,
          storageItems: [],
          storageTypeId: '',
          isActive: true,
          properties: {},
          hasProperties: false,
          propertyCount: 0,
          isAvailableForStorage: true,
          createdAt: '',
          updatedAt: '',
        } as any);
      } else {
        setEditData(null);
      }
      setOpenEdit(true);
    },
    [drawerStorageUnits]
  );

  const handleDelete = useCallback(
    async (rowId: string) => {
      setDeleteLoading(true);
      setDeleteError(null);
      try {
        const res = await DataService.delete(`api/StorageUnit/${rowId}`, {});
        const json = await res.json();
        if (json.success) {
          setDeleteId(null);
          await fetchDetail();
        } else {
          setDeleteError(json.message || 'Failed to delete storage unit');
        }
      } catch (e) {
        setDeleteError('Failed to delete storage unit');
      } finally {
        setDeleteLoading(false);
      }
    },
    [fetchDetail]
  );

  const handleConvertToParent = useCallback(() => {
    setOpenAdd(true);
  }, []);

  const fetchTransferActivity = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      if (!id) {
        return {
          items: [],
          totalCount: 0,
          pageNumber: 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false,
        };
      }

      try {
        const queryParams = new URLSearchParams({
          page: String(params.page + 1),
          pageSize: String(params.pageSize),
        });

        if (params.search && params.search.trim()) {
          queryParams.append('search', params.search.trim());
        }

        if (params.sortModel && Array.isArray(params.sortModel) && params.sortModel.length > 0) {
          const sortItem = params.sortModel[0];
          if (sortItem && sortItem.field) {
            queryParams.append('sortBy', sortItem.field);
            queryParams.append('sortDirection', sortItem.sort || 'desc');
          }
        }

        const response = await DataService.get(
          `api/Operation/storage-unit/${id}/transfers?${queryParams.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch transfers`);
        }

        const result = await response.json();

        if (result && result.items) {
          const returnData = {
            items: result.items.map((item: any) => ({
              id: item.id,
              unitId: item.unitId,
              itemName: item.itemName || 'Unknown Product',
              itemBarcode: item.unitBarcode || item.barcode,
              quantity: item.quantity,
              direction: item.direction,
              sourceType: item.sourceType,
              sourceId: item.sourceId,
              sourceName: item.sourceName || 'Unknown Source',
              destinationType: item.destinationType,
              destinationId: item.destinationId,
              destinationName: item.destinationName || 'Unknown Destination',
              boxRefNumber: item.boxRefNumber,
              timestamp: item.timestamp,
              notes: item.notes,
              referenceId: item.referenceId,
              operationType: item.operationType,
              operationId: item.operationId,
            })) as Transfer[],
            totalCount: result.totalCount || result.items?.length || 0,
            pageNumber: result.pageNumber || params.page + 1,
            pageSize: result.pageSize || params.pageSize,
            totalPages: result.totalPages || Math.ceil((result.items?.length || 0) / params.pageSize),
            hasNext: result.hasNext || false,
            hasPrevious: result.hasPrevious || false,
          };

          return returnData;
        }

        throw new Error('Invalid response format');
      } catch (error) {
        console.error('Error fetching transfer activity:', error);
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false,
        };
      }
    },
    [id]
  );

  const getOperationTypeInfo = (value: number | string) => {
  const v = Number(value);
  switch (v) {
    case 1: return { label: 'Move In', color: 'success' as const };
    case 2: return { label: 'Move Out', color: 'warning' as const };
    case 3: return { label: 'Reallocation', color: 'secondary' as const };
    case 4: return { label: 'Shipment', color: 'info' as const };
    case 5: return { label: 'Transfer', color: 'primary' as const };
    default: return { label: 'Unknown', color: 'default' as const };
  }
};



  const activityColumns = useMemo<PaginatedDataGridColumn[]>(
    () => [
      { field: 'timestamp', headerName: 'Date & Time', width: 180, type: 'dateTime' },
      {
        field: 'operationType',
        headerName: 'Type',
        minWidth: 140,
        renderCell: (params: GridRenderCellParams) => {
          const { label, color } = getOperationTypeInfo(params.value);
          return <Chip label={label} size="small" color={color as any} variant="outlined" />;
        },
        sortComparator: (a: any, b: any) => Number(a) - Number(b),
      },
      {
        field: 'direction',
        headerName: 'Direction',
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.value === 1 ? 'Incoming' : 'Outgoing'}
            color={params.value === 1 ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: 'itemName',
        headerName: 'Product',
        flex: 1.5,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Box
            sx={{
              cursor: params.row.unitId ? 'pointer' : 'default',
              '&:hover': { textDecoration: params.row.unitId ? 'underline' : 'none' },
            }}
            onClick={() => params.row.unitId && navigate(`/units/${params.row.unitId}`)}
          >
            <Typography variant="body2" fontWeight="medium">
              {params.value || 'Unknown Product'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(params.row.itemBarcode as string) || 'N/A'}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'quantity',
        headerName: 'Quantity',
        width: 100,
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            color={params.row.direction === 1 ? 'success.main' : 'error.main'}
            fontWeight="medium"
          >
            {params.row.direction === 1 ? `+${params.value}` : `-${params.value}`}
          </Typography>
        ),
      },
      {
        field: 'boxRefNumber',
        headerName: 'Container',
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">
            {params.value || 'Independent'}
          </Typography>
        ),
      },
      { field: 'sourceName', headerName: 'From', flex: 1, minWidth: 150 },
      { field: 'destinationName', headerName: 'To', flex: 1, minWidth: 150 },
    ],
    [navigate]
  );

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  // Calculate tab indices dynamically
  const tabConfig = useMemo(() => {
    const tabs = [
      { key: 'details', label: 'Details', icon: <StorageIcon />, alwaysShow: true }
    ];

    if (hasInventoryItems) {
      tabs.push({
        key: 'inventory',
        label: `Storage Inventory (${storageItemsList.length})`,
        icon: <InventoryIcon />,
        alwaysShow: false
      });
    }

    if (hasChildUnits) {
      tabs.push({
        key: 'storage-units',
        label: `Storage Units (${childUnitsList.length})`,
        icon: <StorageIcon />,
        alwaysShow: false
      });
    }

    tabs.push({
      key: 'activity',
      label: 'Activity',
      icon: <TimelineIcon />,
      alwaysShow: true
    });

    return tabs;
  }, [hasInventoryItems, hasChildUnits, storageItemsList.length, childUnitsList.length]);

  const getCurrentTabContent = () => {
    const currentTab = tabConfig[activeTab];
    if (!currentTab) return null;

    switch (currentTab.key) {
      case 'details':
        return renderDetailsTab();
      case 'inventory':
        return renderInventoryTab();
      case 'storage-units':
        return renderStorageUnitsTab();
      case 'activity':
        return renderActivityTab();
      default:
        return null;
    }
  };

  const renderDetailsTab = () => (
    <Box>
      {/* Show convert to parent action if no inventory items and no child units */}
      {hasNeither && permissions.canAdd && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity="info"
            action={
              <Button
                variant="contained"
                startIcon={<TransformIcon />}
                onClick={handleConvertToParent}
                size="small"
              >
                Convert to Parent
              </Button>
            }
          >
            This storage unit is empty. You can convert it to a parent unit by adding child storage units.
          </Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Properties Card - Similar to Product Detail */}
        {data?.properties && Object.keys(data.properties).length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Properties
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(data.properties).map(([key, value], index) => {
                    const colorVariants = ['blue', 'purple', 'green', 'pink', 'orange', 'cyan'];
                    const colorIndex = index % colorVariants.length;
                    const color = colorVariants[colorIndex];

                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Paper sx={{
                          p: 1.5,
                          bgcolor: `${color}.50`,
                          border: 1,
                          borderColor: `${color}.200`,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ color: `${color}.600` }} fontWeight="bold">
                              {formatPropertyName(key)}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {String(value)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom color="primary">
              Basic Information
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Code
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data?.code}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data?.name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">{data?.description || 'No description provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Warehouse
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                   {data?.warehouse?.name || data?.parentWarehouseName || '-'}
                </Typography>
              </Box>
              {data?.level !== 1 && data?.parentStorageUnitName && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Parent Storage Unit
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.parentStorageUnitName}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom color="primary">
              Technical Details
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Storage Type
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {data?.storageType?.name || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Level
                </Typography>
                <Chip label={`Level ${data?.level}`} size="small" color="primary" variant="outlined" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Chip
                  label={data?.isLeaf ? 'Leaf Unit' : 'Parent Unit'}
                  size="small"
                  color={data?.isLeaf ? 'success' : 'secondary'}
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={data?.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  color={data?.isActive ? 'success' : 'error'}
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Available for Storage
                </Typography>
                <Chip
                  label={data?.isAvailableForStorage ? 'Yes' : 'No'}
                  size="small"
                  color={data?.isAvailableForStorage ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Box>
              {data?.aggregatedUniqueProducts !== undefined && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Unique Products
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.aggregatedUniqueProducts}
                  </Typography>
                </Box>
              )}
              {data?.aggregatedTotalQuantity !== undefined && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Quantity
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.aggregatedTotalQuantity}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderInventoryTab = () => (
    <Box>
      <PaginatedDataGrid
        key={`storage-items-${refreshKey}`}
        title="Storage Items"
        columns={storageItemColumns}
        rows={storageItemsList}
        idField="id"
        useBackendPagination={false}
        wmsMode
        densityLevel="comfortable"
        striped
        maxHeight={600}
        onRowClick={(params) => params.row.itemId && navigate(`/units/${params.row.unitId}`)}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
      />
    </Box>
  );

  const renderStorageUnitsTab = () => (
    <StorageUnitTable
      data={tableData}
      actions={
        permissions.canAdd
          ? [
              {
                key: 'addStorageUnit',
                title: 'Add Storage Unit',
                icon: <AddIcon />,
                color: 'secondary',
                onClick: () => setOpenAdd(true),
                visible: () => true,
                disabled: () => false,
              },
            ]
          : []
      }
      onEdit={permissions.canEdit ? handleEdit : undefined}
      onDelete={permissions.canDelete ? handleDelete : undefined}
      visibleColumns={['code', 'name', 'isLeaf', 'UniqueProducts', 'TotalQuantity','actions'] as any}
    />
  );

  const renderActivityTab = () => (
    <PaginatedDataGrid
      key={`activity-${refreshKey}-${id}`}
      title="Transfer Activity"
      columns={activityColumns}
      idField="id"
      useBackendPagination
      onDataFetch={fetchTransferActivity}
      wmsMode
      densityLevel="comfortable"
      striped
      maxHeight={600}
      initialState={{
        pagination: { paginationModel: { pageSize: 15 } },
        sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
      }}
      // noDataMessage="No transfer activity found for this storage unit"
    />
  );

  const storageItemCount = storageItemsList.length;
  
  if (loading) return <Loader />;
  if (notFound)
    return (
      <StatusCard
        layout="page"
        variant="not-found"
        imageSrc={process.env.PUBLIC_URL + '/static/images/status/error1.svg'}
        title="Storage Unit Not Found"
        message="Sorry, this storage unit doesn't exist. Check the code or go back."
        onAction={() => nav('/storage-unit')}
        actionLabel="Back to List"
      />
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Left side: Back arrow + title + status */}
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{
                      bgcolor: 'grey.100',
                      '&:hover': { bgcolor: 'grey.200' },
                    }}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>

                  <Typography variant="h5" fontWeight={600}>
                    {data.code}
                  </Typography>

                  <Chip
                    label={data.isActive ? 'Active' : 'Inactive'}
                    color={data.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Stack>
            </Grid>

            {/* Right side: Edit button */}
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {permissions.canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setEditData(data);
                      setOpenEdit(true);
                    }}
                    size="small"
                  >
                    Edit
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          {tabConfig.map((tab, index) => (
            <Tab 
              key={tab.key}
              icon={tab.icon} 
              label={tab.label} 
              iconPosition="start" 
            />
          ))}
        </Tabs>

        {getCurrentTabContent()}
      </Box>

      {openAdd && (
        <CreateStorageUnitDrawer
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onSuccess={() => {
            setOpenAdd(false);
            setTimeout(() => window.location.reload(), 2000);
          }}
          storageUnits={drawerStorageUnits as any}
          defaultWarehouseId={(data as any)?.warehouseId}
          defaultParentStorageUnitId={data?.id}
          infoParentStorageUnit={data ? { Code: data.code, Name: data.name } : undefined}
          forceParentWarehouseId={(data as any)?.warehouseId}
          forceParentStorageUnitId={data?.id}
        />
      )}

      {openEdit && editData && (
        <UpdateStorageUnitDrawer
            open={openEdit}
            onClose={() => {
              setOpenEdit(false);
              setEditData(null);
            }}
            onSuccess={() => {
              setOpenEdit(false);
              setEditData(null);
              window.location.reload();
            }}
            updateData={editData}
            warehouses={
              data?.warehouse
                ? [{ Id: data.warehouse.id, Name: data.warehouse.name }]
                : []
            }
            storageUnits={drawerStorageUnits as any}
            warehouseName={data?.warehouse?.name || ''}
            forceParentWarehouseId={data?.warehouse?.id}
          />
      )}

      {deleteId && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 2, background: '#fff' }}>
          <Typography>Are you sure you want to delete this storage unit?</Typography>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
          <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
            <button
              onClick={() => handleDelete(deleteId)}
              disabled={deleteLoading}
              style={{
                color: 'white',
                background: 'red',
                border: 'none',
                padding: '6px 16px',
                borderRadius: 4,
                cursor: deleteLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteId(null)}
              disabled={deleteLoading}
              style={{
                color: 'black',
                background: '#eee',
                border: 'none',
                padding: '6px 16px',
                borderRadius: 4,
                cursor: deleteLoading ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StorageUnitDetailPage;